import ElementController from '../base-controllers/ElementController';
import { getData, selectSingleFrom } from '../utils/dom';
import { TOGGLE_CONTROLLER_ID, TOGGLE_EVENT_NAME } from '../../constants';

const TOGGLE_CLASS_DATA_ATTR = 'toggle-class';
const TOGGLE_ON_DATA_ATTR = 'toggle-on';
const OPEN_FOCUS_DATA_ATTR = 'open-focus';
const OPENED_TEXT_DATA_ATTR = 'opened-text';
const CLOSED_TEXT_DATA_ATTR = 'closed-text';
const OPENED_ARIA_LABEL_DATA_ATTR = 'opened-aria-label';
const CLOSED_ARIA_LABEL_DATA_ATTR = 'closed-aria-label';
const CLOSE_OUTSIDE_DATA_ATTR = 'close-on-outside-click';
const CLOSE_ESCAPE_DATA_ATTR = 'close-on-escape';
const HANDLE_ARIA_HIDDEN_DATA_ATTR = 'handle-aria-hidden';

/**
 * Controller for generic toggle buttons that toggles class names.
 *
 * Mostly expects the toggled class name to trigger a show/hide of something,
 * hence the 'open' and 'close' terminology.
 *
 * Options via data attributes:
 *
 * - toggle-class (string) The class name to toggle.
 * - toggle-on (string) Which element to toggle the class name on. Can be 'self'
 *   to toggle on the button itself, 'parent' to toggle on the button's parent
 *   element or a CSS selector for any other target.
 * - opened-text (string) Button text to set when the toggle is open.
 * - closed-text (string) Button text to set when the toggle is closed.
 *   Will be set on the button on init.
 * - opened-aria-label (string) `aria-label` to set when the toggle is open.
 * - closed-aria-label (string) `aria-label` to set when the toggle is closed.
 *   Will be set on the button on init.
 * - close-on-outside-click (boolean) If the class name should be toggled when
 *   clicking outside the toggle target. Defaults to true.
 * - handle-aria-hidden (boolean) If `aria-hidden` should be set when toggling.
 *   It will also be set on page load and resize depending on the target's
 *   visibility.
 *
 * Example with all options:
 *
 *     <button
 *       type="button"
 *       data-controller="Toggle"
 *       data-toggle-class="thing-open"
 *       data-toggle-on="parent"
 *       aria-controls="thing-dialog"
 *       data-opened-text="{% trans 'Close' %}"
 *       <!-- closed-text can be omitted if it's the same as the initial
 *       button text -->
 *       data-closed-text="{% trans 'Open' %}"
 *       data-opened-aria-label="{% trans 'Close thing' %}"
 *       <!-- closed-aria-label can be omitted if it's the same as the initial
 *       aria-label text -->
 *       data-closed-aria-label="{% trans 'Open thing' %}"
 *       <!-- REQUIRES aria-controls. close-on-outside-click defaults to true
 *       and can be omitted unless it should be disabled -->
 *       data-close-on-outside-click="false"
 *       <!-- REQUIRES aria-controls. close-on-escape defaults to true and can
 *       be omitted unless it should be disabled -->
 *       data-close-on-escape="false"
 *       <!-- Selector for node inside the target to focus when opened -->
 *       data-open-focus="input"
 *       data-handle-aria-hidden="true"
 *       aria-label="{% trans 'Open thing' %}"
 *     >
 *       <span class="text">{% trans "Open" %}</span>
 *       {% icon "chevron-down" %}
 *     </button>
 *
 * Events triggered on root node (data-controller="Toggle"):
 *
 * | Name              | Arguments | Triggered                 |
 * |-------------------|-----------|---------------------------|
 * | TOGGLE_EVENT_NAME | isOpen    | When the class is toggled |
 */
export default class Toggle extends ElementController {
  static id = TOGGLE_CONTROLLER_ID;

  /**
   * DOM loaded, bind the events.
   */
  init() {
    const btn = this.getRoot();

    // Settings
    this.toggleClass = getData(btn, TOGGLE_CLASS_DATA_ATTR);
    this.openFocus = getData(btn, OPEN_FOCUS_DATA_ATTR);
    // Default to true
    this.closeOutside = getData(btn, CLOSE_OUTSIDE_DATA_ATTR) !== false;
    this.closeEscape = getData(btn, CLOSE_ESCAPE_DATA_ATTR) !== false;
    this.handleAriaHidden = getData(btn, HANDLE_ARIA_HIDDEN_DATA_ATTR);

    // Target for the class name toggle
    const targetString = getData(btn, TOGGLE_ON_DATA_ATTR);
    let toggleTarget = document.body;
    if (targetString === 'self') {
      toggleTarget = this.getRoot();
    } else if (targetString === 'parent') {
      toggleTarget = this.getRoot().parentNode;
    } else if (targetString) {
      toggleTarget = document.querySelector(targetString);
    }
    this.toggleTarget = toggleTarget;

    // Target for aria-expanded toggle
    const ariaTargetId = btn.getAttribute('aria-controls');
    let ariaTarget = null;
    if (ariaTargetId) {
      ariaTarget = document.getElementById(ariaTargetId);
      if (ariaTarget) {
        btn.setAttribute('aria-expanded', String(this.isOpen));
      }
    }
    this.ariaTarget = ariaTarget;

    // Target for button text toggle. Try finding a generic `.text` element
    // or fall back to the first child. This is to avoid removing other
    // children like icons that may exist when toggling the text.
    let textTarget = this.getRoot();
    const buttonText = btn.querySelector('.text');
    if (buttonText) {
      textTarget = buttonText;
    } else if (btn.firstElementChild) {
      textTarget = btn.firstElementChild;
    }
    this.textTarget = textTarget;

    // Opened/closed text and aria-label settings. Use initial texts as a
    // defaults for the closed state.
    this.openedText = getData(btn, OPENED_TEXT_DATA_ATTR);
    this.closedText = getData(btn, CLOSED_TEXT_DATA_ATTR);
    if (this.openedText && !this.closedText) {
      this.closedText = this.textTarget.innerHTML;
    }
    this.openedAriaLabel = getData(btn, OPENED_ARIA_LABEL_DATA_ATTR);
    this.closedAriaLabel = getData(btn, CLOSED_ARIA_LABEL_DATA_ATTR);
    if (this.openedAriaLabel && !this.closedAriaLabel) {
      this.closedAriaLabel = btn.getAttribute('aria-label');
    }

    // Set initial aria-label and text since the data attributes can be set
    // without the actual values being set. This can be a way of progressive
    // enhancement, setting different value only if the toggle can actually
    // be used.
    if (this.closedAriaLabel) {
      btn.setAttribute('aria-label', this.closedAriaLabel);
    }
    if (this.closedText) {
      this.textTarget.innerHTML = this.closedText;
    }

    // Ensure open state on load. The HTML may only have the open class set
    // and not toggled text etc.
    if (this.isOpen) {
      this.open();
    }

    // ------------------- Bind events ------------------- //
    // Ensure all bound events are removed in destroy().
    this.on(this.getRoot(), 'click', this.handleClick);
    if (this.closeOutside) {
      this.on(document, 'click', this.handleOutsideClick);
    }
    if (this.closeEscape && this.ariaTarget) {
      this.on(this.ariaTarget, 'keydown', this.handleTargetKeydown);
    }
    // Handle aria-hidden on page load and after resize
    if (this.handleAriaHidden && this.ariaTarget) {
      this._windowResizeTimer = null;
      this.on(window, 'resize', this.handleWindowResize);
      this.checkAriaHiddenState();
    }

    // ------------------- Configuration errors ------------------- //
    if (process.env.NODE_ENV === 'development') {
      if (ariaTargetId && !this.ariaTarget) {
        throw new Error(
          `Toggle button has aria-controls set to '${ariaTargetId}', but there is no node with that ID.`,
        );
      }
      if (this.handleAriaHidden && !this.ariaTarget) {
        throw new Error(
          `Can't use '${HANDLE_ARIA_HIDDEN_DATA_ATTR}' without aria-controls.`,
        );
      }
      if (this.openFocus && !this.ariaTarget) {
        throw new Error(
          `Can't use '${OPEN_FOCUS_DATA_ATTR}' without aria-controls.`,
        );
      }
    }
  }

  /**
   * Check if the toggle target is in its open/active state.
   *
   * @readonly
   * @returns {boolean}
   */
  get isOpen() {
    return this.toggleTarget.classList.contains(this.toggleClass);
  }

  /**
   * Close and remove event listeners when destroyed.
   *
   * @returns {boolean} True if successful.
   */
  destroy() {
    this.close();

    this.off(this.getRoot(), 'click', this.handleClick);
    if (this.closeOutside) {
      this.off(document, 'click', this.handleOutsideClick);
    }
    if (this.closeEscape && this.ariaTarget) {
      this.off(this.ariaTarget, 'keydown', this.handleTargetKeydown);
    }
    if (this.handleAriaHidden && this.ariaTarget) {
      this.off(window, 'resize', this.handleWindowResize);
    }

    return true;
  }

  /**
   * 'Open' the toggle by adding the class name and setting attributes.
   */
  open() {
    const btn = this.getRoot();
    this.toggleTarget.classList.add(this.toggleClass);

    if (this.openedText) {
      this.textTarget.innerHTML = this.openedText;
    }

    if (this.ariaTarget) {
      btn.setAttribute('aria-expanded', 'true');
      if (this.handleAriaHidden) {
        this.ariaTarget.setAttribute('aria-hidden', 'false');
      }
    }

    if (this.openedAriaLabel) {
      btn.setAttribute('aria-label', this.openedAriaLabel);
    }

    if (this.openFocus && this.ariaTarget) {
      const focusNode = selectSingleFrom(this.ariaTarget, this.openFocus);
      if (focusNode) {
        focusNode.focus();
      } else if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(
          `Trying to focus '${this.openFocus}' but there is no node matching that selector`,
        );
      }
    }

    this.triggerCustomEvent(this.getRoot(), TOGGLE_EVENT_NAME, true);
  }

  /**
   * 'Close' the toggle by removing the class name and setting attributes.
   */
  close() {
    const btn = this.getRoot();
    this.toggleTarget.classList.remove(this.toggleClass);

    if (this.closedText) {
      this.textTarget.innerHTML = this.closedText;
    }

    if (this.ariaTarget) {
      btn.setAttribute('aria-expanded', 'false');
      if (this.handleAriaHidden) {
        this.ariaTarget.setAttribute('aria-hidden', 'true');
      }
    }

    if (this.closedAriaLabel) {
      btn.setAttribute('aria-label', this.closedAriaLabel);
    }

    this.triggerCustomEvent(this.getRoot(), TOGGLE_EVENT_NAME, false);
  }

  /**
   * Toggle button click callback.
   *
   * @param {Event} e - Click event.
   */
  handleClick = (e) => {
    e.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  /**
   * Close toggles that don't contain the current click target.
   *
   * @param {Event} e - Click event.
   */
  handleOutsideClick = (e) => {
    // The toggle has the necessary 'flags' and the click target is not the
    // toggle button or the toggle target.
    if (
      this.isOpen &&
      this.ariaTarget &&
      !this.ariaTarget.contains(e.target) &&
      !this.getRoot().contains(e.target)
    ) {
      this.close();
    }
  };

  /**
   * Close when Escape is pressed inside the target.
   *
   * @param {Event} e - Keydown event.
   */
  handleTargetKeydown = (e) => {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
      this.getRoot().focus();
    }
  };

  /**
   * Set aria-hidden attribute depending on visibility state.
   */
  checkAriaHiddenState = () => {
    if (this.ariaTarget) {
      const targetStyle = getComputedStyle(this.ariaTarget);
      // Visibility, display and zero size should count as hidden.
      // https://webaim.org/techniques/css/invisiblecontent/
      const isHidden =
        targetStyle.visibility === 'hidden' ||
        targetStyle.display === 'none' ||
        parseInt(targetStyle.width, 10) === 0 ||
        parseInt(targetStyle.height, 10) === 0;
      this.ariaTarget.setAttribute('aria-hidden', String(isHidden));
    }
  };

  /**
   * Run debounced callbacks on window resize.
   */
  handleWindowResize = () => {
    clearTimeout(this._windowResizeTimer);
    this._windowResizeTimer = setTimeout(this.checkAriaHiddenState, 200);
  };
}
